import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { supabase } from "@/integrations/supabase/client";

let initialized = false;

/**
 * Native Apple sign-in needs JWT credentials (Services ID, Team ID, Key ID and
 * the .p8 key) configured on the backend Apple provider. Until those exist the
 * button would always fail on device, so it is hidden inside the native app.
 * Flip this to `true` the moment the credentials are configured.
 */
export const NATIVE_APPLE_SIGN_IN_ENABLED = false;

function describeError(error: unknown) {
  if (error instanceof Error) return { name: error.name, message: error.message, code: (error as { code?: string }).code };
  if (error && typeof error === "object") return error;
  return { message: String(error) };
}

async function initializeApple(): Promise<void> {
  if (initialized) return;
  console.info("[NativeAppleAuth] initializing SocialLogin Apple provider");
  await SocialLogin.initialize({
    apple: {},
  });
  initialized = true;
}

export function isNativeAppleAuthAvailable(): boolean {
  try {
    const isNativeIos = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
    if (isNativeIos && !Capacitor.isPluginAvailable("SocialLogin")) {
      console.warn("[NativeAppleAuth] SocialLogin plugin is not installed in this iOS build; rebuild/sync the native app");
    }
    return isNativeIos;
  } catch {
    return false;
  }
}

export async function signInWithNativeApple(): Promise<{ error: Error | null }> {
  try {
    console.info("[NativeAppleAuth] Starting native Apple sign-in via SocialLogin");
    if (!Capacitor.isPluginAvailable("SocialLogin")) {
      return { error: new Error("The iOS app needs to be rebuilt with the native Apple sign-in plugin") };
    }

    await initializeApple();

    const login = await SocialLogin.login({
      provider: "apple",
      options: { scopes: ["email", "name"] },
    });

    const idToken = (login.result as { idToken?: string }).idToken;
    if (!idToken) {
      return { error: new Error("Apple did not return an identity token") };
    }

    const { error } = await supabase.auth.signInWithIdToken({
      provider: "apple",
      token: idToken,
    });

    if (error) return { error };
    console.info("[NativeAppleAuth] native Apple sign-in completed");
    return { error: null };
  } catch (e) {
    console.error("[NativeAppleAuth] Native Apple sign-in failed", describeError(e));
    const error = e instanceof Error ? e : new Error("Native Apple sign-in failed");
    return { error };
  }
}
